import React, { Component, ErrorInfo, ReactNode } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw, Home, Copy, Check } from "lucide-react";
import { captureException, addBreadcrumb } from "@/lib/sentry";
import { useState } from "react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /** Context name for better error grouping */
  context?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

function ErrorDetails({ error, errorInfo }: { error: Error; errorInfo: ErrorInfo | null }) {
  const [copied, setCopied] = useState(false);

  const errorText = `Error: ${error.message}\n\nStack Trace:\n${error.stack}\n\nComponent Stack:${errorInfo?.componentStack || "N/A"}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(errorText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-muted p-4 rounded-lg">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-medium text-destructive">
          Error: {error.message}
        </p>
        <Button
          size="sm"
          variant="ghost"
          onClick={handleCopy}
          className="h-8"
        >
          {copied ? (
            <>
              <Check className="h-3 w-3 mr-1" />
              Copied
            </>
          ) : (
            <>
              <Copy className="h-3 w-3 mr-1" />
              Copy
            </>
          )}
        </Button>
      </div>
      {errorInfo && (
        <details className="text-xs text-muted-foreground">
          <summary className="cursor-pointer hover:text-foreground">
            View error details
          </summary>
          <pre className="mt-2 overflow-auto max-h-64 p-2 bg-background rounded">
            {errorInfo.componentStack}
          </pre>
        </details>
      )}
    </div>
  );
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log to console
    console.error(
      `[ErrorBoundary${this.props.context ? ` ${this.props.context}` : ""}]:`,
      error,
      errorInfo
    );

    // Add breadcrumb for context
    addBreadcrumb(
      `Error in ${this.props.context || "component"}`,
      "error",
      "error",
      {
        componentStack: errorInfo.componentStack,
        errorMessage: error.message,
      }
    );

    // Capture in Sentry with context
    captureException(error, {
      errorBoundary: this.props.context || "default",
      componentStack: errorInfo.componentStack,
    });

    this.setState({
      error,
      errorInfo,
    });
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  private handleGoHome = () => {
    window.location.href = "/";
  };

  private handleReload = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-background">
          <Card className="max-w-2xl w-full">
            <CardHeader>
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-8 w-8 text-destructive" />
                <div>
                  <CardTitle className="text-2xl">Something went wrong</CardTitle>
                  <CardDescription>
                    We encountered an unexpected error. Please try refreshing the page.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {this.state.error && (
                <ErrorDetails error={this.state.error} errorInfo={this.state.errorInfo} />
              )}

              <div className="flex flex-col sm:flex-row gap-3">
                <Button onClick={this.handleReset} className="flex-1">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Try Again
                </Button>
                <Button onClick={this.handleReload} variant="outline" className="flex-1">
                  Reload Page
                </Button>
                <Button onClick={this.handleGoHome} variant="outline" className="flex-1">
                  <Home className="h-4 w-4 mr-2" />
                  Go Home
                </Button>
              </div>

              <div className="text-xs text-muted-foreground text-center">
                If this problem persists, please contact{" "}
                <a href="mailto:support@hticu.org" className="text-primary hover:underline">
                  support@hticu.org
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
